import { ProfileUnitGroup } from '~/domain/intensity/lineProfileDatasets';
import { mmToCm } from '~/utils/units';

/** One overlaid curve: display-unit y on a cm x-axis; a null y breaks the line. */
export interface ProfileCurve {
    readonly id: string;
    readonly color: string;
    readonly data: { x: number; y: number | null }[];
}

/**
 * Break a curve at off-grid gaps: where two kept samples are farther apart than
 * ~1.5× the base sample spacing, a stretch was dropped (off-grid / non-finite),
 * so insert a `null`-y point to break the line there instead of drawing a
 * segment across data that does not exist.
 */
export function breakAtGaps(
    points: { x: number; y: number | null }[]
): { x: number; y: number | null }[] {
    if (points.length < 2) {
        return points;
    }
    let minStep = Infinity;
    for (let i = 1; i < points.length; i++) {
        const step = points[i].x - points[i - 1].x;
        if (step > 0 && step < minStep) {
            minStep = step;
        }
    }
    if (!Number.isFinite(minStep)) {
        return points;
    }
    const threshold = minStep * 1.5;
    const out: { x: number; y: number | null }[] = [];
    for (let i = 0; i < points.length; i++) {
        if (i > 0 && points[i].x - points[i - 1].x > threshold) {
            out.push({ x: (points[i - 1].x + points[i].x) / 2, y: null });
        }
        out.push(points[i]);
    }
    return out;
}

/** Curves for one unit group: display-unit y on a shared cm x-axis. */
export function toCurves(group: ProfileUnitGroup): ProfileCurve[] {
    return group.datasets.map((dataset) => ({
        id: dataset.key,
        color: dataset.color,
        data: breakAtGaps(
            dataset.samples.map((sample) => ({
                x: mmToCm(sample.distanceMm),
                y: dataset.toDisplay(sample.value),
            }))
        ),
    }));
}
